// Overspeed Recompute Service
// Thin client for the local `overspeed-service` FastAPI microservice.
//
// The CRA dev server (`src/setupProxy.js`) forwards `/overspeed/*` to
// `http://localhost:8000`, so in development the browser just hits
// relative URLs. In production you'd point this at a deployed instance
// via OVERSPEED_SERVICE_BASE below.

import { fetchIotDeviceDataInPeriod } from './fuelService';

// Leading prefix for the overspeed-service. The CRA dev proxy
// (`src/setupProxy.js`) catches `/overspeed/*` and forwards it to
// `http://localhost:8000`, so the default base is empty and the client
// posts to `/overspeed/recompute` (matching the FastAPI route verbatim).
// In production, override with a fully-qualified base URL.
const OVERSPEED_SERVICE_BASE =
  process.env.REACT_APP_OVERSPEED_SERVICE_BASE || '';

// 48 hour cap is enforced by AssetPro's iot-device-data/in-period endpoint
// (see fuelService.js:122-134). Chunk any longer window client-side.
const CHUNK_MS = 48 * 60 * 60 * 1000;

/**
 * Fetch raw IoT telemetry covering [startTs, endTs] in 48h chunks and
 * concatenate into a single array of raw rows.
 *
 * @param {string} deviceId  AssetPro device IMEI
 * @param {string} assetId   AssetPro asset object id
 * @param {number} startTs   epoch ms
 * @param {number} endTs     epoch ms
 * @returns {Promise<{success: boolean, rows: Array, errors: Array<string>}>}
 */
export const fetchRawTelemetryChunked = async (deviceId, assetId, startTs, endTs) => {
  const errors = [];
  const rows = [];

  if (!deviceId || !assetId) {
    return { success: false, rows: [], errors: ['Missing deviceId or assetId'] };
  }
  if (!startTs || !endTs || startTs >= endTs) {
    return { success: false, rows: [], errors: ['Invalid time range'] };
  }

  let cursor = startTs;
  while (cursor < endTs) {
    const chunkEnd = Math.min(cursor + CHUNK_MS, endTs);
    // eslint-disable-next-line no-await-in-loop
    const result = await fetchIotDeviceDataInPeriod(deviceId, cursor, chunkEnd, assetId);
    if (!result.success) {
      errors.push(`[${cursor}-${chunkEnd}] ${result.error || 'fetch failed'}`);
    } else {
      const payload = result.data;
      let chunkRows = [];
      if (Array.isArray(payload)) {
        chunkRows = payload;
      } else if (payload && Array.isArray(payload.raw)) {
        chunkRows = payload.raw;
      } else if (payload && typeof payload === 'object') {
        const firstArrayKey = Object.keys(payload).find((k) => Array.isArray(payload[k]));
        if (firstArrayKey) chunkRows = payload[firstArrayKey];
      }
      rows.push(...chunkRows);
    }
    cursor = chunkEnd;
  }

  return { success: errors.length === 0, rows, errors };
};

/**
 * POST the raw rows to the Python microservice and get back events +
 * rejected candidates + stage counts.
 *
 * @param {Object} params
 * @param {string} params.deviceId
 * @param {string} params.assetId
 * @param {number} params.startTs
 * @param {number} params.endTs
 * @param {number} params.speedLimitKmph
 * @param {Array}  params.rows           raw AssetPro iot-device-data rows
 * @param {number} [params.peakMarginPct]
 * @param {number} [params.minEventSeconds]
 * @returns {Promise<{success: boolean, data: Object|null, error: string|null}>}
 */
export const postRecompute = async ({
  deviceId,
  assetId,
  startTs,
  endTs,
  speedLimitKmph,
  rows,
  peakMarginPct,
  minEventSeconds,
}) => {
  try {
    const body = {
      device_id: deviceId,
      asset_id: assetId,
      start_ts_ms: startTs,
      end_ts_ms: endTs,
      speed_limit_kmph: speedLimitKmph,
      rows: rows || [],
    };
    if (peakMarginPct !== undefined && peakMarginPct !== null) {
      body.peak_margin_pct = peakMarginPct;
    }
    if (minEventSeconds !== undefined && minEventSeconds !== null) {
      body.min_event_seconds = minEventSeconds;
    }

    const response = await fetch(`${OVERSPEED_SERVICE_BASE}/overspeed/recompute`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const text = await response.text();
      return { success: false, data: null, error: `${response.status} ${text}` };
    }

    const data = await response.json();
    return { success: true, data, error: null };
  } catch (error) {
    return { success: false, data: null, error: error.message || String(error) };
  }
};

/**
 * End-to-end convenience: fetch raw rows for the window, then recompute.
 *
 * @param {Object} params — same shape as postRecompute but without `rows`.
 * @returns {Promise<{success: boolean, data: Object|null, error: string|null, rowCount: number, fetchErrors: Array<string>}>}
 */
export const recomputeForAsset = async ({
  deviceId,
  assetId,
  startTs,
  endTs,
  speedLimitKmph,
  peakMarginPct,
  minEventSeconds,
}) => {
  const fetchResult = await fetchRawTelemetryChunked(deviceId, assetId, startTs, endTs);
  if (fetchResult.rows.length === 0) {
    return {
      success: false,
      data: null,
      error: fetchResult.errors.join('; ') || 'No telemetry rows returned',
      rowCount: 0,
      fetchErrors: fetchResult.errors,
    };
  }

  const recomputed = await postRecompute({
    deviceId,
    assetId,
    startTs,
    endTs,
    speedLimitKmph,
    rows: fetchResult.rows,
    peakMarginPct,
    minEventSeconds,
  });

  return {
    success: recomputed.success,
    data: recomputed.data,
    error: recomputed.error,
    rowCount: fetchResult.rows.length,
    fetchErrors: fetchResult.errors,
    // Raw telemetry is returned so the caller can cache it and replay
    // individual events on a map without another round-trip to AssetPro.
    rows: fetchResult.rows,
  };
};
